import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon, PageEyebrow } from '../../ui/index.js';
import type { infra } from '@photo-vault/shared';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { MediaGrid } from '../viewer/MediaGrid.js';
import { decryptRating } from '../rating/ratingCrypto.js';
import { decryptTagCached } from './tagCache.js';

interface PhotoRow {
  photo: dto.Photo;
  filename: string;
  mimeType?: string;
  url: string | null;
  rating: number | null;
}

interface Loaded {
  albumId: string;
  rootAlbumId: string;
  albumKey: CryptoKey;
  tagId: string;
  tagName: string;
  rows: PhotoRow[];
  // Server-issued cursor for the next page; absent once the gallery is fully
  // loaded.
  nextCursor?: string;
  // True while paging in additional photos (from the "Carica altri" button).
  loadingMore: boolean;
}

// Owner-only gallery for a single tag. Reads /tags/{id}/photos page-by-page
// and renders the result through MediaGrid (same fluid pipeline as album
// detail). Read-only here: editing tag membership happens at the photo
// level, not via this gallery.
export function TagDetailPage() {
  const { albumId, tagId } = useParams<{ albumId: string; tagId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [state, setState] = useState<Loaded | 'loading' | { error: string }>('loading');

  useEffect(() => {
    if (!albumId || !tagId) return;
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const { albums } = await metadata.listAlbums(session.jwt);
        const self = albums.find((a) => a.id === albumId);
        if (!self) {
          if (!cancelled) setState({ error: 'album non trovato' });
          return;
        }
        const root = albums.find((a) => a.id === self.rootAlbumId);
        if (!root || !root.wrappedAlbumKey) {
          if (!cancelled) setState({ error: 'chiave radice mancante' });
          return;
        }
        const albumKey = await getAlbumKey(root.rootAlbumId, root.wrappedAlbumKey, session.rsaPriv);

        // Fetch the tag definition + first photo page in parallel — both gated
        // on the same K_album so they don't add latency to one another.
        const [tagDef, firstPage] = await Promise.all([
          metadata.getTag(tagId, root.rootAlbumId, session.jwt),
          metadata.listTagPhotos(tagId, root.rootAlbumId, session.jwt),
        ]);
        if (cancelled) return;

        const decryptedTag = await decryptTagCached(tagDef, root.rootAlbumId, albumKey);
        const rows = await hydratePage(
          metadata,
          firstPage.photos,
          albumKey,
          session.jwt,
          () => cancelled,
        );
        if (cancelled) return;

        setState({
          albumId,
          rootAlbumId: root.rootAlbumId,
          albumKey,
          tagId,
          tagName: decryptedTag.name,
          rows,
          ...(firstPage.nextCursor ? { nextCursor: firstPage.nextCursor } : {}),
          loadingMore: false,
        });
      } catch (err) {
        if (!cancelled) setState({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [albumId, tagId, session.jwt, session.rsaPriv, metadata]);

  if (!albumId || !tagId) return null;
  if (state === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in state) return <p className="py-6 text-sm text-error">Errore: {state.error}</p>;

  async function loadMore() {
    if (typeof state !== 'object' || 'error' in state) return;
    if (!state.nextCursor || state.loadingMore) return;
    setState({ ...state, loadingMore: true });
    try {
      const page = await metadata.listTagPhotos(
        state.tagId,
        state.rootAlbumId,
        session.jwt,
        state.nextCursor,
      );
      const more = await hydratePage(
        metadata,
        page.photos,
        state.albumKey,
        session.jwt,
        () => false,
      );
      setState((curr) => {
        if (typeof curr !== 'object' || 'error' in curr) return curr;
        const merged = [...curr.rows, ...more];
        const next: Loaded = {
          ...curr,
          rows: merged,
          loadingMore: false,
        };
        if (page.nextCursor) next.nextCursor = page.nextCursor;
        else delete (next as { nextCursor?: string }).nextCursor;
        return next;
      });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
      setState((curr) =>
        typeof curr === 'object' && !('error' in curr) ? { ...curr, loadingMore: false } : curr,
      );
    }
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>tag</PageEyebrow>
        <Breadcrumb albumId={state.albumId} />
        <h2 className="font-display text-2xl font-semibold">{state.tagName}</h2>
        <p className="text-sm text-on-surface-variant">
          {state.rows.length === 0
            ? 'Nessuna foto con questo tag.'
            : state.rows.length === 1
              ? '1 foto'
              : `${state.rows.length} foto`}
          {state.nextCursor ? ' (parziale)' : null}
        </p>
      </div>

      {state.rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="photo_library" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">
            Nessuna foto associata a questo tag. Apri una foto e usa "Modifica tag".
          </p>
        </Card>
      ) : (
        <MediaGrid
          items={state.rows.map((r) => ({
            id: r.photo.id,
            url: r.url,
            filename: r.filename,
            ...(r.mimeType ? { mimeType: r.mimeType } : {}),
            rating: r.rating,
            info: {
              encryptedMeta: r.photo.encryptedMeta,
              uploadedAt: r.photo.createdAt,
              encryptedSize: r.photo.size,
              ...(r.photo.keyGen !== undefined ? { keyGen: r.photo.keyGen } : {}),
            },
          }))}
          albumKey={state.albumKey}
        />
      )}

      {state.nextCursor ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => void loadMore()}
            loading={state.loadingMore}
            leftIcon={<Icon name="expand_more" />}
          >
            Carica altri
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Breadcrumb({ albumId }: { albumId: string }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to="/albums" className="text-on-surface-variant transition-colors hover:text-cyan">
        Album
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <Link
        to={`/albums/${encodeURIComponent(albumId)}`}
        className="text-on-surface-variant transition-colors hover:text-cyan"
      >
        Dettaglio
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <Link
        to={`/albums/${encodeURIComponent(albumId)}/tags`}
        className="text-on-surface-variant transition-colors hover:text-cyan"
      >
        Tag
      </Link>
    </nav>
  );
}

// Decrypt photo metadata (filename + rating) and stream the presigned URLs
// in via the existing downloads/batch helper so tiles render as soon as the
// first chunk lands. Mirrors AlbumPhotosSection's reload pattern.
async function hydratePage(
  metadata: infra.MetadataStore,
  photos: dto.Photo[],
  albumKey: CryptoKey,
  jwt: string,
  isCancelled: () => boolean,
): Promise<PhotoRow[]> {
  if (photos.length === 0) return [];
  const decryptedMeta = await Promise.all(
    photos.map(async (p) => {
      const ratingPromise = p.encryptedRating
        ? decryptRating(p.encryptedRating, albumKey)
        : Promise.resolve<number | null>(null);
      try {
        const json = await cv.decryptText(p.encryptedMeta, albumKey);
        const meta = JSON.parse(json) as { filename?: string; mimeType?: string };
        const rating = await ratingPromise;
        return { photo: p, filename: meta.filename ?? p.id, mimeType: meta.mimeType, rating };
      } catch {
        const rating = await ratingPromise;
        return { photo: p, filename: '(meta non leggibile)', rating };
      }
    }),
  );
  const rows: PhotoRow[] = decryptedMeta.map((m) => ({
    photo: m.photo,
    filename: m.filename,
    ...('mimeType' in m && m.mimeType ? { mimeType: m.mimeType } : {}),
    url: null,
    rating: m.rating,
  }));
  rows.sort((a, b) => a.photo.createdAt.localeCompare(b.photo.createdAt));

  // Stream presigned URLs into the rows by mutating the array — callers wrap
  // this in setState before this function resolves, so the fan-out only
  // matters for a deferred refresh of the parent state. We collect into a
  // local map and let the parent set state once at the end; loading more
  // photos doesn't need the streaming UX of first-paint.
  const urlByPhotoId = new Map<string, string>();
  await metadata.downloadsBatchAllStreaming(
    photos.map((p) => ({ albumId: p.albumId, photoId: p.id })),
    jwt,
    (chunk) => {
      if (isCancelled()) return;
      for (const it of chunk) urlByPhotoId.set(it.photoId, it.url);
    },
  );
  for (const r of rows) {
    const u = urlByPhotoId.get(r.photo.id);
    if (u) r.url = u;
  }
  return rows;
}
